import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  styled,
  Card,
  CardContent,
  TextField,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import { Comment, Dataset } from "../types";

interface CommentCardProps {
  comment: Comment;
  onClose: () => void;
  onDelete?: (commentId: string) => void;
  onEdit?: (commentId: string, text: string) => void;
  onPrev?: () => void;
  onNext?: () => void;
  showNavigation?: boolean;
  isReadOnly?: boolean;
  markdownComponents?: Components;
  initialEditMode?: boolean;
  dataset?: Dataset | null; // Датасет лекции
  onAddToDataset?: (commentId: number) => void; // Функция для добавления комментария в датасет
  onRemoveFromDataset?: (commentId: number) => void; // Функция для удаления комментария из датасета
  isInDataset?: boolean; // Флаг, указывающий, находится ли комментарий в датасете
}

// Стилизованные компоненты в соответствии с DeepSchool
const StyledCard = styled(Card)(({ theme }: any) => ({
  width: "100%",
  maxWidth: "750px",
  position: "relative",
  zIndex: 1000,
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
  borderRadius: "12px",
  overflow: "visible",
  backgroundColor: "#FFFFFF", // Белый фон как на сайте DeepSchool
  color: "#333333", // Темный текст
  border: "1px solid #E5E7EB",
}));

const CommentText = styled(Box)(({ theme }: any) => ({
  marginTop: "8px",
  marginBottom: "16px",
  fontSize: "16px",
  lineHeight: 1.6,
  wordBreak: "break-word",
  "& a": {
    color: "#0E1B37", // Темно-синий цвет для ссылок
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  "& code": {
    backgroundColor: "#F5F7FA",
    padding: "2px 4px",
    borderRadius: "4px",
    fontFamily: '"Roboto Mono", monospace',
    wordBreak: "break-all",
    color: "#0E1B37", // Темно-синий цвет для кода
  },
  "& pre": {
    backgroundColor: "#F5F7FA",
    padding: "16px",
    borderRadius: "8px",
    overflow: "auto",
    maxWidth: "100%",
    border: "1px solid #E5E7EB",
  },
  "& img": {
    maxWidth: "100%",
    height: "auto",
    borderRadius: "8px",
  },
}));

const ActionButton = styled(Button)(({ theme }: any) => ({
  backgroundColor: "#F5F7FA", // Светло-серый фон
  color: "#0E1B37", // Темно-синий цвет
  textTransform: "none",
  fontWeight: 500,
  padding: "8px 16px",
  borderRadius: "8px",
  "&:hover": {
    backgroundColor: "rgba(14, 27, 55, 0.1)",
  },
  marginRight: "8px",
}));

const NavigationButton = styled(IconButton)(({ theme }: any) => ({
  color: "#0E1B37", // Темно-синий цвет
  "&:hover": {
    backgroundColor: "rgba(14, 27, 55, 0.1)",
  },
  padding: "8px",
}));

const ButtonsContainer = styled(Box)(({ theme }: any) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginTop: "16px",
}));

export const CommentCard: React.FC<CommentCardProps> = ({
  comment,
  onClose,
  onDelete,
  onEdit,
  onPrev,
  onNext,
  showNavigation = false,
  isReadOnly = false,
  markdownComponents,
  initialEditMode = false,
  dataset = null,
  onAddToDataset,
  onRemoveFromDataset,
  isInDataset = false,
}) => {
  const [isEditing, setIsEditing] = useState(initialEditMode);
  const [editedText, setEditedText] = useState(comment.text);

  // Сбрасываем состояние редактирования при изменении комментария
  useEffect(() => {
    setEditedText(comment.text);
    setIsEditing(initialEditMode);
  }, [comment.id, comment.text, initialEditMode]);

  // Добавляем обработчик клавиши Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (isEditing) {
          setIsEditing(false);
          setEditedText(comment.text);
        } else {
          onClose();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, isEditing, comment.text]);

  const handleSaveEdit = () => {
    if (onEdit) {
      onEdit(String(comment.id), editedText);
      setIsEditing(false);
    }
  };

  // Функция для добавления/удаления комментария из датасета
  const handleDatasetAction = () => {
    if (!dataset) return;

    if (isInDataset && onRemoveFromDataset) {
      // Добавляем подтверждение при удалении из датасета
      if (
        window.confirm(
          "Вы уверены, что хотите удалить комментарий из датасета?"
        )
      ) {
        onRemoveFromDataset(comment.id);
      }
    } else if (!isInDataset && onAddToDataset) {
      onAddToDataset(comment.id);
    }
  };

  return (
    <StyledCard
      data-comment-id={comment.id}
      data-comment-cell-id={comment.cellId}
      data-comment-line-number={comment.lineNumber}
    >
      <CardContent sx={{ pt: 2, pb: 2, position: "relative" }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, color: "#333333", mr: 1 }}
          >
            Комментарий
          </Typography>

          {showNavigation && (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {onPrev && (
                <NavigationButton
                  size="small"
                  onClick={onPrev}
                  aria-label="previous comment"
                  sx={{ padding: "2px" }}
                >
                  <ArrowBackIcon fontSize="small" />
                </NavigationButton>
              )}
              {onNext && (
                <NavigationButton
                  size="small"
                  onClick={onNext}
                  aria-label="next comment"
                  sx={{ padding: "2px" }}
                >
                  <ArrowForwardIcon fontSize="small" />
                </NavigationButton>
              )}
            </Box>
          )}
        </Box>

        {isEditing ? (
          <>
            <TextField
              multiline
              fullWidth
              value={editedText}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setEditedText(e.target.value)
              }
              variant="outlined"
              size="small"
              autoFocus
              onKeyDown={(e: React.KeyboardEvent) => {
                // Предотвращаем всплытие событий клавиш стрелок при редактировании
                if (
                  e.key === "ArrowLeft" ||
                  e.key === "ArrowRight" ||
                  e.key === "ArrowUp" ||
                  e.key === "ArrowDown"
                ) {
                  e.stopPropagation();
                }
              }}
              sx={{
                mt: 1,
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#E5E7EB",
                    borderRadius: "8px",
                  },
                  "&:hover fieldset": {
                    borderColor: "#0E1B37",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#0E1B37",
                  },
                },
              }}
            />
            <Box sx={{ display: "flex", gap: 1 }}>
              <ActionButton size="small" onClick={handleSaveEdit}>
                Сохранить
              </ActionButton>
              <ActionButton
                size="small"
                onClick={onClose}
                sx={{ backgroundColor: "#FFF5F5", color: "#FF3B30" }}
              >
                Отмена
              </ActionButton>
            </Box>
          </>
        ) : (
          <>
            <CommentText>
              <ReactMarkdown components={markdownComponents}>
                {comment.text}
              </ReactMarkdown>
            </CommentText>

            <ButtonsContainer>
              <Box sx={{ display: "flex", gap: 1 }}>
                {!isReadOnly && onEdit && (
                  <ActionButton
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => setIsEditing(true)}
                  >
                    Редактировать
                  </ActionButton>
                )}
                {!isReadOnly && onDelete && (
                  <ActionButton
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => onDelete(String(comment.id))}
                    sx={{ backgroundColor: "#FFF5F5", color: "#FF3B30" }}
                  >
                    Удалить
                  </ActionButton>
                )}
                {dataset && (onAddToDataset || onRemoveFromDataset) && (
                  <Tooltip
                    title={
                      isInDataset
                        ? `Удалить из датасета "${dataset?.title || ""}"`
                        : `Добавить в датасет "${dataset?.title || ""}"`
                    }
                  >
                    <IconButton
                      size="small"
                      onClick={handleDatasetAction}
                      sx={{
                        color: isInDataset ? "#4CAF50" : "#0E1B37",
                        padding: "4px",
                        "&:hover": {
                          backgroundColor: isInDataset
                            ? "rgba(76, 175, 80, 0.1)"
                            : "rgba(14, 27, 55, 0.1)",
                        },
                      }}
                    >
                      {isInDataset ? (
                        <CheckIcon fontSize="small" />
                      ) : (
                        <AddIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
                {!dataset && (onAddToDataset || onRemoveFromDataset) && (
                  <Tooltip title="Для этой лекции не выбран датасет">
                    <span>
                      <IconButton
                        size="small"
                        disabled={true}
                        sx={{
                          color: "#9E9E9E",
                          cursor: "default",
                          padding: "4px",
                        }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Box>
            </ButtonsContainer>
          </>
        )}
      </CardContent>
    </StyledCard>
  );
};
